import { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { AuthContext } from '../../../../../context/authContext';
import { SearchMatContext } from '../../../../../context/searchMatContext';
import otrosApiService from '../../../../../services/http/otrosApiService';
import matriculacionApiService from '../../../../../services/http/matriculacionApiService';
import { getItem, removeItem, STORAGE_KEYS } from '../../../../../services/storage';
import colors from '../../../../../theme/colors';

const DetailButton = ({ onClick }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px',
      fontSize: '0.7rem', fontWeight: 600, borderRadius: '6px',
      border: `1px solid ${colors.primary}`, backgroundColor: '#fff',
      color: colors.primary, cursor: 'pointer', transition: 'all 0.15s',
    }}
  >
    Detalles
  </button>
);

DetailButton.propTypes = {
  onClick: PropTypes.func.isRequired,
};

const buildDelegaciones = (center) => {
  let delegaciones = '';
  if (center && center !== '' && Array.isArray(center)) {
    center.forEach((opcion) => {
      delegaciones += delegaciones === '' ? `'${opcion.label}'` : `,'${opcion.label}'`;
    });
  }
  return delegaciones;
};

const buildBaseParams = ({
  name,
  lastname,
  dni,
  email,
  idRequest,
  delegaciones,
  deudores,
  center,
  state,
  course,
}) => {
  const params = {
    nombre: name,
    apellidos: lastname,
    dni,
    email,
    id: idRequest,
    idRequest,
    nombreDelegacion: delegaciones,
    deudor: deudores,
  };
  if (center && center !== '') params.sede = center;
  if (state !== '') params.estado = state;
  if (course !== '') params.idPlan = course;
  return params;
};

const buildParamsByHeadquarter = ({
  bd,
  userOU,
  name,
  lastname,
  dni,
  email,
  idRequest,
  delegaciones,
  deudores,
  center,
  state,
  course,
}) => {
  const base = buildBaseParams({
    name,
    lastname,
    dni,
    email,
    idRequest,
    delegaciones,
    deudores,
    center,
    state,
    course,
  });

  if (
    (userOU.idCedesca === 0 && userOU.idCntec === 0) ||
    (userOU.idCedesca === -1 && userOU.idCntec === -1) ||
    (userOU.idCedesca === -2 && userOU.idCntec === -2) ||
    (userOU.idCedesca === -3 && userOU.idCntec === -3)
  ) {
    return {
      params: { ...base, headquarter: bd },
      params2: null,
    };
  }

  if (bd === 'CEDESCA') {
    return {
      params: { ...base, headquarter: 'CEDESCA', idDelegacion: userOU.idCedesca },
      params2: null,
    };
  }

  if (bd === 'CNTEC') {
    return {
      params: { ...base, headquarter: 'CNTEC', idDelegacion: userOU.idCntec },
      params2: null,
    };
  }

  return {
    params: { ...base, headquarter: 'CEDESCA', idDelegacion: userOU.idCedesca },
    params2: { ...base, headquarter: 'CNTEC', idDelegacion: userOU.idCntec },
  };
};

const useFiltroSolicitudes = () => {
  const navigate = useNavigate();
  const { userOU } = useContext(AuthContext);
  const { searchMatState, saveSearches } = useContext(SearchMatContext);

  const [dataApi, setDataApi] = useState('');
  const [dni, setDni] = useState({ data: '', error: '' });
  const [email, setEmail] = useState({ data: '', error: '' });
  const [name, setName] = useState({ data: '', error: '' });
  const [lastname, setLastname] = useState({ data: '', error: '' });
  const [idRequest, setIdRequest] = useState({ data: '', error: '' });
  const [state, setState] = useState('');
  const [center, setCenter] = useState('');
  const [headquarterList, setHeadquarterList] = useState([]);
  const [bd, setBd] = useState('CEDETECGROUP');
  const [course, setCourse] = useState('');
  const [coursesList, setCoursesList] = useState([]);
  const [centersList, setCentersList] = useState([]);
  const [statesList, setStatesList] = useState([]);
  const [isFormSubmittable, setIsFormSubmittable] = useState(true);
  const [isTableNotLoading, setIsTableNotLoading] = useState(true);
  const [isProceedingLoading, setIsProceedingLoading] = useState(false);
  const [deudores, setDeudores] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const offsetRef = useRef(0);
  const lastSearchParamsRef = useRef(null);

  const PAGE_SIZE = 100;

  const handleRequest = (numMatricula, headquarter) => {
    const empresaLowerCase = headquarter.toLowerCase();
    if (getItem(STORAGE_KEYS.ENROLLMENT)) removeItem(STORAGE_KEYS.ENROLLMENT);
    navigate(`${empresaLowerCase}/${numMatricula}/revision`);
  };

  useEffect(() => {
    const fetchData = async () => {
      const [empresas, ciclos, centros, estados] = await Promise.allSettled([
        otrosApiService.consultaEmpresas({ headquarter: '1' }),
        matriculacionApiService.buscarCiclos({ headquarter: 'CEDESCA' }),
        matriculacionApiService.buscarCentros({ headquarter: 'CEDESCA' }),
        matriculacionApiService.buscarEstados({ headquarter: 'CEDESCA' }),
      ]);

      setHeadquarterList(empresas.status === 'fulfilled' ? empresas.value : []);
      setCoursesList(ciclos.status === 'fulfilled' ? ciclos.value : []);
      setCentersList(
        centros.status === 'fulfilled'
          ? centros.value.map((o) => ({ value: o.id, label: o.descripcion }))
          : [],
      );
      setStatesList(estados.status === 'fulfilled' ? estados.value : []);
    };
    fetchData();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!searchMatState || searchMatState.newSearch !== false) return;

      setName({ data: searchMatState.nameSearch, error: '' });
      setLastname({ data: searchMatState.lastnameSearch, error: '' });
      setState(searchMatState.stateSearch ?? '');
      setCenter(searchMatState.centerSearch ?? '');
      setCourse(searchMatState.courseCenter ?? '');
      setIdRequest({ data: searchMatState.idMatSearch, error: '' });
      setEmail({ data: searchMatState.emailSearch, error: '' });
      setDni({ data: searchMatState.dniSearch, error: '' });
      setBd(searchMatState.dbSearch ?? 'CEDETECGROUP');
      setDeudores(searchMatState.deudores ?? false);
      setIsTableNotLoading(false);

      try {
        const params = {
          headquarter: searchMatState.dbSearch,
          nombre: searchMatState.nameSearch,
          apellidos: searchMatState.lastnameSearch,
          dni: searchMatState.dniSearch,
          email: searchMatState.emailSearch,
          id: searchMatState.idMatSearch,
          idDelegacion: userOU.idCedesca > 0 ? userOU.idCedesca : userOU.idCntec,
          deudor: searchMatState.deudores,
        };
        if (searchMatState.centerSearch && searchMatState.centerSearch !== '') {
          params.sede = searchMatState.centerSearch;
        }
        if (searchMatState.stateSearch !== '') params.estado = searchMatState.stateSearch;
        if (searchMatState.idMatSearch !== '') params.idRequest = searchMatState.idMatSearch;
        if (searchMatState.courseCenter !== '') params.idPlan = searchMatState.courseCenter;
        if (parseInt(userOU.idCedesca, 10) > 0) {
          params.nombreDelegacion = userOU.centro;
        }

        const data = await matriculacionApiService.filtrarSolicitudes({ ...params, limit: PAGE_SIZE, offset: 0 });
        data.forEach((element) => {
          element.actions = (
            <DetailButton onClick={() => handleRequest(element.num_matricula, element.headquarter)} />
          );
        });
        setDataApi(data);
        setHasMore(data.length >= PAGE_SIZE);
        offsetRef.current = data.length;
      } catch (error) {
        setDataApi('');
      } finally {
        setIsTableNotLoading(true);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchMatState?.newSearch]);

  const handleNameOnChange = (e) => {
    const value = e.target.value.trim();
    if (value.length === 0) {
      setName({ data: '', error: '' });
      setIsFormSubmittable(true);
    } else if (!value.match(/^(?![\s.]+$)[a-zA-ZÀ-ÿ\s.]*$/)) {
      setName({ data: '', error: 'format' });
      setIsFormSubmittable(false);
    } else if (value.length === 1) {
      setName({ data: '', error: 'length' });
      setIsFormSubmittable(false);
    } else {
      setName({ data: value, error: '' });
      setIsFormSubmittable(true);
    }
  };

  const handleLastNameOnChange = (e) => {
    const value = e.target.value.trim();
    if (!value.match(/^(?![\s.]+$)[a-zA-ZÀ-ÿ\s.]*$/)) {
      setLastname({ data: '', error: 'format' });
      setIsFormSubmittable(false);
    } else if (value.length === 1) {
      setLastname({ data: '', error: 'length' });
      setIsFormSubmittable(false);
    } else {
      setLastname({ data: value, error: '' });
      setIsFormSubmittable(true);
    }
  };

  const handleDNIOnChange = (e) => {
    setDni({ data: e.target.value.trim(), error: '' });
    setIsFormSubmittable(true);
  };

  const handleIDOnChange = (e) => {
    setIdRequest({ data: e.target.value.trim(), error: '' });
    setIsFormSubmittable(true);
  };

  const handleEmailOnChange = (e) => {
    const value = e.target.value.trim();
    const emailRegex =
      /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;
    if (value.length > 0 && !value.toLowerCase().match(emailRegex)) {
      setEmail({ data: '', error: 'format' });
      setIsFormSubmittable(false);
    } else {
      setEmail({ data: value, error: '' });
      setIsFormSubmittable(true);
    }
  };

  const handleHeadquarter = (e) => setBd(e.target.value);
  const handleDeudor = () => setDeudores(!deudores);
  const handleCenters = (selected) => setCenter(selected);

  const handleCourses = (e) => {
    setCourse(e.target.value === 'all' ? '' : e.target.value);
  };

  const handleState = (e) => {
    setState(e.target.value === '1' ? '' : e.target.value);
  };

  const handleReset = () => {
    setName({ data: '', error: '' });
    setLastname({ data: '', error: '' });
    setDni({ data: '', error: '' });
    setEmail({ data: '', error: '' });
    setBd('CEDETECGROUP');
    setCourse('');
    setState('');
    setCenter('');
    setIdRequest({ data: '', error: '' });
  };

  const handleOnSubmit = async (e) => {
    if (e !== undefined) e.preventDefault();
    setIsTableNotLoading(false);

    const delegaciones = buildDelegaciones(center);
    const { params, params2 } = buildParamsByHeadquarter({
      bd,
      userOU,
      name: name.data,
      lastname: lastname.data,
      dni: dni.data,
      email: email.data,
      idRequest: idRequest.data,
      delegaciones,
      deudores,
      center,
      state,
      course,
    });

    try {
      const requests = [];
      if (params) requests.push(matriculacionApiService.filtrarSolicitudes({ ...params, limit: PAGE_SIZE, offset: 0 }));
      if (params2) requests.push(matriculacionApiService.filtrarSolicitudes({ ...params2, limit: PAGE_SIZE, offset: 0 }));

      const responses = await Promise.all(requests);
      const results = responses.flat();
      results.forEach((element) => {
        element.actions = (
          <DetailButton onClick={() => handleRequest(element.num_matricula, element.headquarter)} />
        );
      });
      setDataApi(results);
      setHasMore(results.length >= PAGE_SIZE);
      offsetRef.current = results.length;
      lastSearchParamsRef.current = { params, params2 };
    } catch (error) {
      setDataApi('');
      setHasMore(false);
    } finally {
      setIsTableNotLoading(true);
    }

    saveSearches({
      nameSearch: name.data,
      lastnameSearch: lastname.data,
      stateSearch: state,
      centerSearch: center,
      courseCenter: course,
      idMatSearch: idRequest.data,
      emailSearch: email.data,
      dniSearch: dni.data,
      deudores,
      dbSearch: bd,
      newSearch: true,
    });
  };

  const downloadExcel = async () => {
    setIsProceedingLoading(true);
    const delegaciones = buildDelegaciones(center);

    try {
      const params = {
        headquarter: bd,
        nombre: name.data,
        apellidos: lastname.data,
        dni: dni.data,
        email: email.data,
        descarga: true,
        nombreDelegacion: delegaciones,
        deudor: deudores,
      };
      if (center !== '') params.sede = center;
      if (state !== '') params.estado = state;
      if (course !== '') params.idPlan = course;
      if (idRequest.data !== '') params.idRequest = idRequest.data;

      const dataSolicitudes = await matriculacionApiService.filtrarSolicitudes(params);
      const url2 = window.URL.createObjectURL(dataSolicitudes);
      const a = document.createElement('a');
      a.href = url2;
      a.download = 'documentt.xlsx';
      a.click();
      window.URL.revokeObjectURL(url2);
    } catch (error) {
      // no-op
    } finally {
      setIsProceedingLoading(false);
    }
  };

  const loadMore = useCallback(async () => {
    if (!lastSearchParamsRef.current || isLoadingMore || !hasMore) return;
    setIsLoadingMore(true);

    try {
      const { params, params2 } = lastSearchParamsRef.current;
      const requests = [];
      if (params) requests.push(matriculacionApiService.filtrarSolicitudes({ ...params, limit: PAGE_SIZE, offset: offsetRef.current }));
      if (params2) requests.push(matriculacionApiService.filtrarSolicitudes({ ...params2, limit: PAGE_SIZE, offset: offsetRef.current }));

      const responses = await Promise.all(requests);
      const results = responses.flat();
      results.forEach((element) => {
        element.actions = (
          <DetailButton onClick={() => handleRequest(element.num_matricula, element.headquarter)} />
        );
      });
      if (results.length > 0) {
        setDataApi((prev) => [...(Array.isArray(prev) ? prev : []), ...results]);
        offsetRef.current += results.length;
      }
      setHasMore(results.length >= PAGE_SIZE);
    } catch (_err) {
      setHasMore(false);
    } finally {
      setIsLoadingMore(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingMore, hasMore]);

  return {
    dataApi,
    name,
    lastname,
    dni,
    email,
    idRequest,
    state,
    center,
    bd,
    course,
    headquarterList,
    coursesList,
    centersList,
    statesList,
    isFormSubmittable,
    isTableNotLoading,
    isProceedingLoading,
    deudores,
    userOU,
    searchMatState,
    handleNameOnChange,
    handleLastNameOnChange,
    handleDNIOnChange,
    handleIDOnChange,
    handleEmailOnChange,
    handleHeadquarter,
    handleCourses,
    handleState,
    handleCenters,
    handleDeudor,
    handleReset,
    handleOnSubmit,
    downloadExcel,
    loadMore,
    isLoadingMore,
    hasMore,
  };
};

export default useFiltroSolicitudes;
